import React, { useState } from 'react';
import { Course, UserRole, Module } from '../../types/lms.types';
import AssignmentSection from '../Assignment/AssignmentSection';
import AnnouncementSection from '../Announcement/AnnouncementSection';
import GradesAnalytics from '../Analytics/GradesAnalytics';
import LiveClassMock from '../LiveClass/LiveClassMock';
import ModuleDropdown from './ModuleDropdown';
import AddModuleModal from './AddModuleModal';
import CreateContentDropdown from './CreateContentDropdown';
import ContentTabs from './ContentTabs';
import AllContentView from './AllContentView';
import AssessmentsView from './AssessmentsView';
import GlossaryView from './GlossaryView';
import OutcomesView from './OutcomesView';
import { getMockModules } from '../../services/lmsDataService';

interface CourseViewProps {
  course: Course;
  userRole: UserRole;
  onBack: () => void;
}

const CourseView: React.FC<CourseViewProps> = ({ course, userRole, onBack }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showLiveClass, setShowLiveClass] = useState(false);
  const [courseData, setCourseData] = useState(course);
  const [selectedModule, setSelectedModule] = useState('all');
  const [modules, setModules] = useState<Module[]>(getMockModules());
  const [showAddModuleModal, setShowAddModuleModal] = useState(false);

  const handleCreateLiveClass = () => {
    setCourseData(prev => ({ ...prev, isLive: true }));
    setShowLiveClass(true);
  };

  const handleEndLiveClass = () => {
    setCourseData(prev => ({ ...prev, isLive: false }));
    setShowLiveClass(false);
  };

  const handleModuleChange = (moduleId: string) => {
    setSelectedModule(moduleId);
  };

  const handleAddModule = (moduleData: Omit<Module, 'id'>) => {
    const newModule: Module = {
      id: `mod${modules.length}`,
      ...moduleData
    };
    setModules([...modules, newModule]);
  };

  if (showLiveClass) {
    return (
      <LiveClassMock
        course={courseData}
        userRole={userRole}
        onLeave={() => {
          if (userRole === 'teacher') {
            handleEndLiveClass();
          } else {
            setShowLiveClass(false);
          }
        }}
      />
    );
  }

  const canCreateContent = userRole === 'teacher' || userRole === 'admin';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4">
          <button
            onClick={onBack}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            ← Back to Dashboard
          </button>
        </div>
        
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">{courseData.name}</h1>
            <p className="text-gray-600 dark:text-gray-400">Instructor: {courseData.teacher}</p>
            <p className="text-sm text-gray-500 dark:text-gray-500">{courseData.students} students enrolled</p>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-3">
            {/* Create Live Class Button for Teachers */}
            {(userRole === 'teacher' || userRole === 'admin') && !courseData.isLive && (
              <button
                onClick={handleCreateLiveClass}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                🎥 Start Live Class
              </button>
            )}

            {/* Live Class Status */}
            {courseData.isLive && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                    <span className="font-medium text-red-700">Live Class Ongoing</span>
                  </div>
                  <button
                    onClick={() => setShowLiveClass(true)}
                    className="inline-flex items-center px-3 py-1 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    {userRole === 'teacher' ? 'Resume Class' : 'Join Now'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Module Selection Dropdown */}
      <ModuleDropdown
        modules={modules}
        selectedModule={selectedModule}
        onModuleChange={handleModuleChange}
        userRole={userRole}
        onAddModule={() => setShowAddModuleModal(true)}
      />

      {/* Enhanced Navigation Tabs */}
      <ContentTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Create/Add Content Button */}
      {canCreateContent && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-end">
            <CreateContentDropdown
              selectedModule={selectedModule}
              onContentCreated={() => {
                // Refresh content list
                console.log('Content created, refreshing...');
              }}
            />
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 min-h-96">
        {activeTab === 'all' && (
          <AllContentView
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'assessments' && (
          <AssessmentsView
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'materials' && (
          <div className="p-6 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <h2 className="text-xl font-semibold">Course Materials</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[
                { name: 'Lecture Notes - Chapter 1', type: 'PDF', size: '2.4 MB', date: '2 days ago', moduleId: 'mod1' },
                { name: 'Video Tutorial - Basics', type: 'Video', size: '45.2 MB', date: '1 week ago', moduleId: 'mod1' },
                { name: 'Linear Equations Examples', type: 'PDF', size: '1.8 MB', date: '3 days ago', moduleId: 'mod2' },
                { name: 'Quadratic Functions Guide', type: 'PDF', size: '3.2 MB', date: '1 day ago', moduleId: 'mod3' }
              ]
                .filter(material => selectedModule === 'all' || material.moduleId === selectedModule)
                .map((material, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium">{material.name}</h3>
                        <p className="text-sm text-gray-500">{material.type} • {material.size}</p>
                        <p className="text-xs text-gray-400 mt-1">{material.date}</p>
                      </div>
                      <button className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100">
                        Download
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {activeTab === 'announcements' && (
          <AnnouncementSection 
            course={courseData} 
            userRole={userRole} 
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'glossary' && (
          <GlossaryView
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'co' && (
          <OutcomesView
            type="course"
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'po' && (
          <OutcomesView
            type="program"
            course={courseData}
            userRole={userRole}
            selectedModule={selectedModule}
          />
        )}

        {activeTab === 'grades' && (
          <GradesAnalytics 
            course={courseData} 
            userRole={userRole} 
            selectedModule={selectedModule}
          />
        )}
      </div>

      {/* Add Module Modal */}
      <AddModuleModal
        isOpen={showAddModuleModal}
        onClose={() => setShowAddModuleModal(false)}
        onAddModule={handleAddModule}
        existingModules={modules}
      />
    </div>
  );
};

export default CourseView;
